import { Link } from 'react-router-dom';

export function TermsPage() {
  return (
    <>
      <header className="nk-header" id="home">
        <div className="section section-lg section-b-0">
          <div className="section-heading">
            <div className="container">
              <div className="row justify-content-center">
                <div className="col-xl-6 col-lg-8">
                  <div className="position-relative text-center">
                    <h2 className="title text-gradient-heading fw-semibold display-6 mb-4">
                      Terms of Service
                    </h2>
                    <p className="fs-8">
                      By accessing or using our website or SaaS platform
                      (&quot;Service&quot;), you agree to the following Terms of
                      Service (&quot;Terms&quot;). Please read them carefully.
                    </p>
                    <p className="fs-10 text-muted fw-semibold">
                      Last updated: October 14, 2026
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>
      <main className="nk-main">
        <div className="section section-xl section-t-0">
          <div className="section-content">
            <div className="container">
              <div className="row justify-content-center">
                <div className="col-xl-8">
                  <div className="nk-entry">
                    <h5>1. Who We Are</h5>
                    <p>
                      Yapamar is operated by <strong>Yapamar OÜ</strong>, a
                      company offering marketing mix modeling tools designed for
                      clarity, speed, and local market precision.
                    </p>

                    <h5>2. Eligibility &amp; Accounts</h5>
                    <p>To use the platform, you must:</p>
                    <ul>
                      <li>Be at least 18 years old</li>
                      <li>Be authorized to represent your company</li>
                      <li>
                        Provide accurate registration and billing information
                      </li>
                    </ul>
                    <p>
                      You are responsible for keeping your account credentials
                      secure and for all actions taken under your account.
                    </p>

                    <h5>3. Use of the Service</h5>
                    <p>You agree to:</p>
                    <ul>
                      <li>
                        Use the platform solely for legal business purposes
                      </li>
                      <li>
                        Avoid reverse-engineering, tampering, or unauthorized
                        access
                      </li>
                      <li>
                        Refrain from uploading harmful or unlawful content
                      </li>
                      <li>Respect intellectual property rights</li>
                    </ul>
                    <p>
                      Violation may lead to suspension or termination of your
                      access.
                    </p>

                    <h5>4. Data &amp; Privacy Compliance</h5>
                    <p>
                      Yapamar does{' '}
                      <strong>
                        not use any personal data or personally identifiable
                        information (PII)
                      </strong>{' '}
                      in our modeling. All modeling is based on anonymized,
                      aggregated business data only. As a result, our platform
                      is <strong>natively GDPR-compliant</strong>, minimizing
                      legal risk and simplifying data handling. Use of the
                      platform is also governed by our{' '}
                      <Link to="/privacy-policy">Privacy Policy</Link> and{' '}
                      <Link to="/cookie-policy">Cookie Policy</Link>.
                    </p>

                    <p>
                      When users connect their advertising and KPI accounts
                      (such as Google Ads) to Yapamar, they authorize read-only
                      access via the official advertising and KPI platform APIs
                      using OAuth 2.0. We receive data such as ad accounts,
                      campaigns, ad groups, and performance metrics exclusively
                      to build Marketing Mix Models and provide budget
                      optimization insights. Users can disconnect these
                      integrations at any time through their account settings or
                      by revoking access directly in the advertising or KPI
                      platform.
                    </p>

                    <h5>5. Subscription &amp; Payment</h5>
                    <ul>
                      <li>Pricing is listed in EUR and excludes VAT</li>
                      <li>
                        Billing occurs monthly (with a minimum subscription
                        period of 3 or 6 months, depending on the selected plan)
                        or annually, as selected
                      </li>
                      <li>
                        Payments are processed securely by a trusted third-party
                        provider
                      </li>
                      <li>
                        Subscriptions renew automatically unless canceled before
                        the next billing cycle
                      </li>
                      <li>
                        You may cancel at any time, with access determination
                        taking effect at the end of then-current Subscription
                        period
                      </li>
                      <li>
                        No refunds will be issued for partial billing periods
                        unless otherwise agreed
                      </li>
                    </ul>
                    <p>
                      Custom pricing and invoicing may apply for enterprise
                      customers.
                    </p>

                    <h5>6. Intellectual Property</h5>
                    <p>
                      All rights to the platform, underlying models, algorithms,
                      code, and visual materials are owned by Yapamar OÜ.
                      <br />
                      You may not copy, modify, distribute, or resell any part
                      of the Service without prior written consent.
                    </p>

                    <h5>7. Service Availability</h5>
                    <p>
                      We aim to provide uninterrupted access but cannot
                      guarantee 100% uptime. Planned maintenance and rare
                      outages may occur.
                      <br />
                      In such cases, we will make commercially reasonable
                      efforts to restore service promptly.
                    </p>

                    <h5>8. Limitation of Liability</h5>
                    <p>
                      To the extent permitted by law, Yapamar OÜ is not liable
                      for:
                    </p>
                    <ul>
                      <li>Indirect or consequential damages</li>
                      <li>
                        Loss of revenue, profits, or business opportunities
                      </li>
                      <li>
                        Data errors resulting from customer-provided inputs
                      </li>
                      <li>
                        Decisions, actions, or outcomes based on insights,
                        recommendations, or results generated by the platform’s
                        modeling and analytics tools
                      </li>
                    </ul>
                    <p>
                      All analyses, insights, and forecasts provided by Yapamar
                      are based on statistical models and available data, and
                      therefore should be used as decision-support tools, not
                      absolute guarantees.
                    </p>
                    <p>
                      Total liability is limited to one (1) month of
                      subscription fees paid by you.
                    </p>

                    <h5>9. Termination of Service</h5>
                    <p>
                      You may terminate your subscription at any time via your
                      account dashboard or by contacting us at{' '}
                      <a href="mailto:support@yapamar.com">
                        support@yapamar.com
                      </a>
                      .
                    </p>
                    <p>After cancellation:</p>
                    <ul>
                      <li>
                        Your access remains active until the end of your current{' '}
                        <strong>billing cycle</strong>, subject to any remaining
                        minimum subscription period of your plan
                      </li>
                      <li>
                        You may request access to export your data within{' '}
                        <strong>90 days of termination</strong>
                      </li>
                    </ul>
                    <p>
                      Yapamar reserves the right to suspend or terminate access
                      for misuse, unpaid fees, or legal obligations, with
                      reasonable notice.
                    </p>

                    <h5>10. Changes to Terms</h5>
                    <p>
                      We may update these Terms periodically. Changes will be
                      communicated through the platform or via email.
                      <br />
                      Continued use of the Service after updates indicates
                      acceptance of the revised Terms.
                    </p>

                    <h5>11. Contact</h5>
                    <p>For any legal or operational inquiries:</p>
                    <p className="d-flex align-items-center gap-2 mb-1">
                      <em className="icon ni ni-mail" />
                      <a href="mailto:support@yapamar.com">
                        support@yapamar.com
                      </a>
                    </p>
                    <p className="d-flex align-items-center gap-2">
                      <em className="icon ni ni-map-pin" />
                      Sepapaja 6, Tallinn 15551, Estonia
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}
